import { ColumnTypeEnum, MasterRecordsQuery, MasterRecordStage } from "@/codegen/graphql-request"
import { getColumnTypes, getReferenceConfigs } from "@/slice/masterSchemaSlice"
import { RootState } from "@/store"
import { ColumnData, ColumnTypes, ReferenceConfig } from "@/type"
import { convertToDisplay } from "@/util/converter"
import { escapeRegExp } from "@/util/text"
import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"

export type DisplayColumns = { [columnName: string]: string }
export type MasterRecord = MasterRecordsQuery["masterRecords"][0] & { displayColumns: DisplayColumns }

interface FilterOption {
	// 前方一致
	isForwardMatch?: boolean
}

interface Filter {
	value: string
	option?: FilterOption
}

interface FilterColumn {
	[columnName: string]: Filter | undefined
}

export type MasterRecordsState = {
	data: { [tableName: string]: MasterRecord[] }
	sortColumn: { [tableName: string]: { [columnName: string]: "desc" | "asc" | undefined } }
	errors: { [idAndColumnName: string]: string[] | undefined }
	pagination: { [tableName: string]: { startIndex: number; pageSize: number } }
	filterColumn: {
		[tableName: string]: FilterColumn
	}
}

export const initialState: MasterRecordsState = {
	data: {},
	sortColumn: {},
	errors: {},
	pagination: {},
	filterColumn: {},
}

export const masterRecordsSlice = createSlice({
	name: "masterRecord",
	initialState,
	reducers: {
		setData: (
			state,
			action: PayloadAction<{
				tableName: string
				data: MasterRecordsQuery["masterRecords"]
				columnTypes: ColumnTypes
				additional: boolean
			}>,
		) => {
			const { tableName, data, columnTypes, additional } = action.payload

			const records = data.map((r) => {
				const record: MasterRecord = { ...r, displayColumns: {} }
				for (const columnName of Object.keys(r.columns)) {
					if (columnTypes[columnName]) {
						record.displayColumns[columnName] = convertToDisplay(r.columns[columnName], columnTypes[columnName])
					}
				}
				return record
			})
			if (additional) {
				state.data[tableName] = state.data[tableName] ?? []
				state.data[tableName].unshift(...records)
			} else {
				state.data[tableName] = records
			}
		},
		setColumnData: (
			state,
			action: PayloadAction<{
				tableName: string
				recordId: string
				columnName: string
				data: ColumnData
				columnType: ColumnTypeEnum
			}>,
		) => {
			const { tableName, recordId, columnName, data, columnType } = action.payload
			const records = state.data[tableName]
			if (!records) throw `not found table records ${tableName}`
			const record = records.find((r) => r.id == recordId)
			if (!record) throw `not found record ${recordId}`
			record.columns[columnName] = data
			record.displayColumns[columnName] = convertToDisplay(data, columnType)
		},
		setSortColumn: (
			state,
			action: PayloadAction<{ tableName: string; columnName: string; mode: "desc" | "asc" | undefined }>,
		) => {
			const { tableName, columnName, mode } = action.payload
			state.sortColumn[tableName] = { [columnName]: mode }
		},
		setPagination: (state, action: PayloadAction<{ tableName: string; startIndex: number; pageSize: number }>) => {
			const { tableName, startIndex, pageSize } = action.payload
			state.pagination[tableName] = { startIndex, pageSize }
		},
		setFilterColumn: (
			state,
			action: PayloadAction<{
				tableName: string
				columnName: string
				keyword: string | undefined
				isForwardMatch?: boolean
			}>,
		) => {
			const { tableName, columnName, keyword, isForwardMatch } = action.payload
			state.filterColumn[tableName] ||= {}
			state.filterColumn[tableName][columnName] = { value: keyword ?? "", option: { isForwardMatch } }
		},
		resetFilterColumn: (state, action: PayloadAction<{ tableName: string; columnName: string }>) => {
			const { tableName, columnName } = action.payload
			delete state.filterColumn[tableName]?.[columnName]
		},
		resetPagination: (state, action: PayloadAction<{ tableName: string }>) => {
			const { tableName } = action.payload
			if (state.pagination[tableName]) {
				state.pagination[tableName] = { ...state.pagination[tableName], startIndex: 0 }
			} else {
				delete state.pagination[tableName]
			}
		},
		resetSort: (state, action: PayloadAction<{ tableName: string }>) => {
			const { tableName } = action.payload
			state.sortColumn[tableName] = {}
		},
		resetFunction: (state, action: PayloadAction<{ tableName: string }>) => {
			const { tableName } = action.payload
			state.sortColumn[tableName] = {}
			state.filterColumn[tableName] = {}
			if (state.pagination[tableName]) {
				state.pagination[tableName] = { ...state.pagination[tableName], startIndex: 0 }
			} else {
				delete state.pagination[tableName]
			}
		},
		resetFilter: (state, action: PayloadAction<{ tableName: string }>) => {
			const { tableName } = action.payload
			state.filterColumn[tableName] = {}
		},
		setErrors: (
			state,
			action: PayloadAction<{ recordId: string; columnName: string; errors: string[] | undefined }[]>,
		) => {
			for (const { recordId, columnName, errors } of action.payload) {
				state.errors[`${recordId}:${columnName}`] = errors
			}
		},
		setStage: (state, action: PayloadAction<{ tableName: string; recordIds: string[]; stage: MasterRecordStage }>) => {
			const { tableName, recordIds, stage } = action.payload
			const records = state.data[tableName]
			if (!records) throw `not found table records ${tableName}`
			for (const recordId of recordIds) {
				const record = records.find((r) => r.id == recordId)
				if (!record) throw `not found record ${recordId}`
				record.stage = stage
			}
		},
		deleteRecords: (state, action: PayloadAction<{ tableName: string; recordIds: string[] }>) => {
			const { tableName, recordIds } = action.payload
			const records = state.data[tableName]
			if (!records) throw `not found table records ${tableName}`
			state.data[tableName] = records.filter((r) => !recordIds.includes(r.id))
		},
		deleteAllTableRows: (state, action: PayloadAction<{ tableName: string }>) => {
			const { tableName } = action.payload
			state.data[tableName] = []
		},
	},
})

// displayColumnとpkColumnが異なる場合は参照カラムとみなす
const isReference = (referenceConfig: ReferenceConfig): boolean => {
	return referenceConfig && referenceConfig.displayColumn !== referenceConfig.pkColumn
}

/**
 * レコードを検索キーワードでフィルターする
 */
export function filterRecord(
	state: RootState,
	tableName: string,
	records: MasterRecord[],
	referenceConfigs: { [columnName: string]: ReferenceConfig },
): MasterRecord[] {
	const columnFilters = columnFilter(state, tableName)
	if (Object.keys(columnFilters).length === 0) return records

	const matchedRecords: MasterRecord[][] = Object.entries(columnFilters).map(([filterColumnName, filterKeyword]) => {
		if (filterKeyword === undefined) return records

		// フィルター対象のカラムが参照カラムの場合、参照テーブルのデータを検索してマッチしたものを返す。
		const referenceConfig = referenceConfigs[filterColumnName]
		const keyword = new RegExp(
			// 前方一致の場合は先頭に^をつける
			filterKeyword.option?.isForwardMatch
				? `^${escapeRegExp(filterKeyword.value.trim())}`
				: escapeRegExp(filterKeyword.value.trim()),
			"i",
		)

		// 参照がない場合
		if (!isReference(referenceConfig)) {
			return records.filter(({ displayColumns }) => keyword.test(displayColumns[filterColumnName]))
		}

		// 参照がある場合
		const refRecords: ReferenceRecord[] = getReferenceRecords(state, referenceConfig) ?? []
		const matchedRefPkSet = new Set(
			refRecords.filter(({ displayData }) => keyword.test(displayData)).map(({ pkData }) => pkData),
		)

		// 参照カラムがリスト以外の場合
		const filterColumnType = getColumnTypes(state)[tableName][filterColumnName]
		if (filterColumnType !== ColumnTypeEnum.List) {
			return records.filter(({ columns }) => matchedRefPkSet.has(columns[filterColumnName]))
		}

		// 参照カラムが複数ある場合は、JSON.parseして配列にして検索する
		return records.filter(({ columns }) => {
			const list = JSON.parse(columns[filterColumnName])
			return list.some((item: unknown) => matchedRefPkSet.has(`${item}`))
		})
	})

	return records.filter((record) => {
		return matchedRecords.every((matchedRecord) => matchedRecord.includes(record))
	})
}

export const displayRecords = (state: RootState, tableName: string) => {
	let records = state.masterRecords.data[tableName] ?? []
	const referenceConfigs = getReferenceConfigs(state, tableName)
	const allColumnTypes = getColumnTypes(state)

	// filter
	records = filterRecord(state, tableName, records, referenceConfigs)

	// sort
	const typeCast = (v: string, columnType: ColumnTypeEnum): string | number => {
		if (columnType === ColumnTypeEnum.Number) {
			return !v ? 0 : parseInt(v, 10)
		} else {
			return v
		}
	}

	/**
	 * Stringが指定されていても、レコードのデータが全て数字だった場合はNumberを指定する。
	 * @param records
	 * @param defaultColumnType
	 * @returns
	 */
	const castColumnType = (records: MasterRecord[], defaultColumnType: ColumnTypeEnum): ColumnTypeEnum => {
		if (
			defaultColumnType === ColumnTypeEnum.String &&
			records.every((data) => /^-?\d+\.?\d*$/.test(data.displayColumns[sortColumnName]))
		) {
			return ColumnTypeEnum.Number
		}
		return defaultColumnType
	}

	const sortColumn = state.masterRecords.sortColumn[tableName] ?? {}
	const sortColumnName = Object.keys(sortColumn)[0]
	const mode = sortColumn[sortColumnName]
	let columnType = allColumnTypes[tableName][sortColumnName]

	const allowReferenceSortType = [ColumnTypeEnum.Number, ColumnTypeEnum.String]
	const referenceConfig = referenceConfigs[sortColumnName]
	if (
		referenceConfig &&
		referenceConfig.displayColumn != referenceConfig.pkColumn &&
		allowReferenceSortType.includes(columnType)
	) {
		// 参照先のデータでソートする場合
		let refRecords = getReferenceRecords(state, referenceConfig) ?? []
		const refColumnType = allColumnTypes[referenceConfig.tableName][sortColumnName]
		const s = mode == "desc" ? [-1, 0, 1] : [1, 0, -1]
		refRecords = [...refRecords].sort((a, b) => {
			const av = typeCast(a.displayData, refColumnType)
			const bv = typeCast(b.displayData, refColumnType)
			if (av > bv) return s[0]
			if (av == bv) return s[1]
			return s[2]
		})
		const rList: MasterRecord[] = []
		refRecords.forEach(({ pkData }) => {
			rList.push(...(records.filter((r) => r.columns[sortColumnName] == pkData) ?? []))
		})
		records = rList
	} else {
		columnType = castColumnType(records, columnType)
		const s = mode == "desc" ? [-1, 0, 1] : [1, 0, -1]
		records = [...records].sort((a, b) => {
			const av = typeCast(a.displayColumns[sortColumnName], columnType)
			const bv = typeCast(b.displayColumns[sortColumnName], columnType)
			if (av > bv) return s[0]
			if (av == bv) return s[1]
			return s[2]
		})
	}

	// pagination
	const { startIndex, pageSize } = pagination(state, tableName)

	return { records: records.slice(startIndex, startIndex + pageSize), size: records.length }
}

export const useReferenceRecords = (referenceConfig: ReferenceConfig) => {
	return useSelector((state: RootState) => getReferenceRecords(state, referenceConfig))
}

interface ReferenceRecord {
	recordId: string
	pkData: string
	displayData: string
}
export const getReferenceRecords = (state: RootState, referenceConfig: ReferenceConfig): ReferenceRecord[] => {
	const records = state.masterRecords.data[referenceConfig.tableName]
	if (!records) return []
	return records.map((record) => {
		const pkData = record.columns[referenceConfig.pkColumn]
		const displayData = record.displayColumns[referenceConfig.displayColumn]
		return {
			recordId: record.id,
			pkData: String(pkData),
			displayData: String(displayData),
		}
	})
}

export const useErrors = (recordId: string, columnName: string) => {
	return useSelector((state: RootState) => state.masterRecords.errors[`${recordId}:${columnName}`])
}

export const usePagination = (tableName: string) => {
	return useSelector((state: RootState) => pagination(state, tableName))
}
export const pagination = (state: RootState, tableName: string) => {
	return state.masterRecords.pagination[tableName] ?? { startIndex: 0, pageSize: 25 }
}

export const useColumnFilter = (tableName: string) => {
	return useSelector((state: RootState) => columnFilter(state, tableName))
}

export const columnFilter = (state: RootState, tableName: string): FilterColumn => {
	return state.masterRecords.filterColumn[tableName] ?? {}
}
